import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { Slider } from '@/components/ui/slider';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FlaskConical, Zap, AlertTriangle, Shield, ArrowUpRight, ArrowDownRight, TrendingDown } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function Delta({ base, sim, suffix = '', invert = false }) {
  const d = sim - base; if (Math.abs(d) < 0.01) return null;
  const pos = invert ? d < 0 : d > 0;
  return <span className={`text-[11px] font-sans font-semibold ${pos ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{d > 0 ? '+' : ''}{Math.abs(d) >= 100 ? fmt(d) : d.toFixed(2)}{suffix}</span>;
}

const PRESETS = {
  custom: { label: 'Custom', ir: 0, vac: 0, rent: 0, exp: 0, cap: 0, def: 0 },
  mild: { label: 'Mild Downturn', ir: 0.5, vac: 3, rent: -3, exp: 2, cap: 0.3, def: 2 },
  severe: { label: 'Severe Recession', ir: 1.5, vac: 8, rent: -12, exp: 5, cap: 0.8, def: 8 },
  rateShock: { label: 'Rate Shock +1.5%', ir: 1.5, vac: 0, rent: 0, exp: 0, cap: 0, def: 0 },
  crisis: { label: '2008-Style', ir: 2.0, vac: 12, rent: -18, exp: 6, cap: 1.5, def: 12 },
};

export default function ExitLab() {
  const { assets: ctxAssets, loading: pfLoading } = usePortfolio();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('custom');
  const [ir, setIr] = useState(0);
  const [vac, setVac] = useState(0);
  const [rent, setRent] = useState(0);
  const [exp, setExp] = useState(0);
  const [cap, setCap] = useState(0);
  const [def, setDef] = useState(0);
  const [execMode, setExecMode] = useState(false);

  useEffect(() => {
    if (pfLoading) return;
    setAssets(ctxAssets);
    setLoading(false);
  }, [pfLoading, ctxAssets]);

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS[id]; setIr(p.ir); setVac(p.vac); setRent(p.rent); setExp(p.exp); setCap(p.cap); setDef(p.def);
  };

  const intel = useMemo(() => {
    if (!assets.length) return null;
    // Base
    let bTv = 0, bTd = 0, bNoi = 0, bAds = 0, bCf = 0;
    assets.forEach(a => { const m = a.metrics || {}; bTv += m.market_value || 0; bTd += m.loan_amount || 0; bNoi += m.noi || 0; bAds += m.annual_debt_service || 0; bCf += m.net_cashflow || 0; });
    const bLtv = bTv > 0 ? bTd / bTv * 100 : 0, bDscr = bAds > 0 ? bNoi / bAds : 0, bDy = bTd > 0 ? bNoi / bTd * 100 : 0;
    const bIrr = (() => { const tw = assets.reduce((s, a) => s + Math.max((a.metrics?.equity || 0), 1), 0); return assets.reduce((s, a) => s + (a.metrics?.irr || 0) * Math.max((a.metrics?.equity || 0), 1) / tw, 0); })();

    // Stressed
    let sTv = 0, sTd = 0, sNoi = 0, sAds = 0;
    assets.forEach(a => {
      const m = a.metrics || {}, la = m.loan_amount || 0, origIr = m.interest_rate || 0;
      const mv = (m.market_value || 0) * (1 - cap / 100);
      const ar = (a.annual_rent || 0) * (1 + rent / 100) * (1 - def / 100);
      const effRent = ar * (1 - Math.min(vac + (a.vacancy_rate || 0), 100) / 100);
      const oc = (a.operating_costs || 0) * (1 + exp / 100);
      const noi = effRent - oc;
      const ads = mp(la, (origIr + ir) / 100, m.loan_term || 30) * 12;
      sTv += mv; sTd += la; sNoi += noi; sAds += ads;
    });
    const sCf = sNoi - sAds;
    const sLtv = sTv > 0 ? sTd / sTv * 100 : 0, sDscr = sAds > 0 ? sNoi / sAds : 0, sDy = sTd > 0 ? sNoi / sTd * 100 : 0;
    const sIrr = bIrr * (sCf / Math.max(bCf, 1));
    const sEq = sTv - sTd;
    const covBuffer = ((sDscr - 1.25) / 1.25 * 100);
    const breakEvenRent = bNoi > 0 ? ((bNoi - bAds) / bNoi * -100 + 100) : 0;

    // Breakpoints
    const breakpoints = [];
    for (let r = 0; r <= 4; r += 0.1) {
      const testAds = assets.reduce((s, a) => s + mp(a.metrics?.loan_amount || 0, ((a.metrics?.interest_rate || 0) + r) / 100, a.metrics?.loan_term || 30) * 12, 0);
      if (testAds > 0 && bNoi / testAds < 1.25) { breakpoints.push({ type: 'DSCR < 1.25', trigger: `+${r.toFixed(1)}% interest`, value: `${(bNoi / testAds).toFixed(2)}x` }); break; }
    }
    for (let v = 0; v <= 30; v += 1) {
      const testNoi = assets.reduce((s, a) => s + (a.annual_rent || 0) * (1 - Math.min(v + (a.vacancy_rate || 0), 100) / 100) - (a.operating_costs || 0), 0);
      if (testNoi - bAds < 0) { breakpoints.push({ type: 'Negative Cashflow', trigger: `${v}% vacancy`, value: fmt(testNoi - bAds) }); break; }
    }
    for (let rd = 0; rd >= -30; rd -= 1) {
      const testNoi = assets.reduce((s, a) => s + (a.annual_rent || 0) * (1 + rd / 100) * (1 - (a.vacancy_rate || 0) / 100) - (a.operating_costs || 0), 0);
      const testIrr = bIrr * ((testNoi - bAds) / Math.max(bCf, 1));
      if (testIrr < 8) { breakpoints.push({ type: 'IRR < 8%', trigger: `${rd}% rent`, value: `${testIrr.toFixed(1)}%` }); break; }
    }

    // Monte Carlo
    const mcRuns = 500;
    const mcResults = [];
    for (let i = 0; i < mcRuns; i++) {
      const rIr = ir + (Math.random() - 0.5) * 1.5;
      const rVac = vac + Math.random() * 6;
      const rRent = rent + (Math.random() - 0.5) * 10;
      const rExp = exp + Math.random() * 3;
      let simNoi = 0, simAds = 0;
      assets.forEach(a => {
        const m = a.metrics || {}, la = m.loan_amount || 0;
        const ar = (a.annual_rent || 0) * (1 + rRent / 100);
        const effR = ar * (1 - Math.min(rVac + (a.vacancy_rate || 0), 100) / 100);
        const noi = effR - (a.operating_costs || 0) * (1 + rExp / 100);
        const ads = mp(la, ((m.interest_rate || 0) + rIr) / 100, m.loan_term || 30) * 12;
        simNoi += noi; simAds += ads;
      });
      const simDscr = simAds > 0 ? simNoi / simAds : 0;
      const simCf = simNoi - simAds;
      const simIrr = bIrr * (simCf / Math.max(bCf, 1));
      mcResults.push({ dscr: simDscr, cf: simCf, irr: simIrr });
    }
    const pDscrBreach = mcResults.filter(r => r.dscr < 1.25).length / mcRuns * 100;
    const pNegCf = mcResults.filter(r => r.cf < 0).length / mcRuns * 100;
    const pIrrLow = mcResults.filter(r => r.irr < 8).length / mcRuns * 100;
    const sortedCf = mcResults.map(r => r.cf).sort((a, b) => a - b);
    const p10Cf = sortedCf[Math.floor(mcRuns * 0.1)];
    const p90Cf = sortedCf[Math.floor(mcRuns * 0.9)];
    const sortedIrr = mcResults.map(r => r.irr).sort((a, b) => a - b);
    const p10Irr = sortedIrr[Math.floor(mcRuns * 0.1)];
    const p90Irr = sortedIrr[Math.floor(mcRuns * 0.9)];

    // MC distribution
    const mcDist = [];
    const cfMin = sortedCf[0], cfMax = sortedCf[sortedCf.length - 1], cfRange = cfMax - cfMin || 1;
    for (let b = 0; b < 20; b++) {
      const lo = cfMin + (b / 20) * cfRange, hi = cfMin + ((b + 1) / 20) * cfRange;
      mcDist.push({ bucket: `${(lo/1e3).toFixed(0)}k`, count: sortedCf.filter(r => r >= lo && r < hi).length, lo });
    }

    // Resilience Score
    const levBuf = Math.min(20, Math.round((70 - sLtv) * 0.8));
    const cfStab = Math.min(20, sCf > 0 ? 18 : 5);
    const divScore = Math.min(20, 14);
    const expDisp = Math.min(20, 12);
    const liqRun = Math.min(20, Math.round(Math.min(435000 / Math.max(sAds / 12, 1), 24) * 0.8));
    const resilience = Math.max(0, levBuf + cfStab + divScore + expDisp + liqRun);
    const survivalMonths = Math.round(435000 / Math.max(sAds / 12, 1));

    return {
      bTv, bTd, bNoi, bAds, bCf, bLtv, bDscr, bDy, bIrr,
      sTv, sTd, sNoi, sAds, sCf, sLtv, sDscr, sDy, sIrr, sEq, covBuffer, breakEvenRent,
      breakpoints,
      pDscrBreach, pNegCf, pIrrLow, p10Cf, p90Cf, p10Irr, p90Irr, mcDist,
      resilience, levBuf, cfStab, divScore, expDisp, liqRun, survivalMonths,
    };
  }, [assets, ir, vac, rent, exp, cap, def]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!intel) return <div className="text-center py-20 text-[#8C877F]">No data.</div>;

  const params = [
    { l: 'Interest Rate', v: ir, set: setIr, min: -1, max: 4, step: 0.1, suffix: '%', plus: ir > 0 },
    { l: 'Vacancy', v: vac, set: setVac, min: 0, max: 20, step: 0.5, suffix: '%' },
    { l: 'Rent Change', v: rent, set: setRent, min: -25, max: 10, step: 1, suffix: '%', plus: rent > 0 },
    { l: 'Expense Inflation', v: exp, set: setExp, min: 0, max: 10, step: 0.5, suffix: '%' },
    { l: 'Cap Rate Shift', v: cap, set: setCap, min: 0, max: 3, step: 0.1, suffix: '%' },
    { l: 'Tenant Default', v: def, set: setDef, min: 0, max: 15, step: 1, suffix: '%' },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="exit-lab-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Risk Simulation & Decision Engine</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Stress Test Lab</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setExecMode(!execMode)} className={`px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium border transition-all ${execMode ? 'bg-[#1F3A34] text-white border-[#1F3A34]' : 'border-[#D9D3C8] text-[#5A5A5A]'}`}>Executive Mode</button>
        </div>
      </div>

      {/* Presets + Sliders */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Scenario Parameters</p>
          <div className="flex gap-1.5">
            {Object.entries(PRESETS).map(([id, p]) => (
              <button key={id} onClick={() => applyPreset(id)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium transition-all ${preset === id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>{p.label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {params.map(p => (
            <div key={p.l} className="space-y-2">
              <div className="flex justify-between"><span className="text-[12px] text-[#5A5A5A] font-sans">{p.l}</span><span className="text-[13px] font-sans font-semibold text-[#1F3A34]">{p.plus ? '+' : ''}{p.v}{p.suffix}</span></div>
              <Slider value={[p.v]} onValueChange={v => { p.set(v[0]); setPreset('custom'); }} min={p.min} max={p.max} step={p.step}
                className="[&_[role=slider]]:bg-[#1F3A34] [&_[role=slider]]:border-0 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3" />
            </div>
          ))}
        </div>
      </div>

      {/* Impact Panel + Resilience */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: 'DSCR', b: intel.bDscr, s: intel.sDscr, good: 1.25, warn: 1.1 },
            { l: 'LTV', b: intel.bLtv, s: intel.sLtv, suffix: '%', good: 100, warn: 100, inv: true },
            { l: 'Debt Yield', b: intel.bDy, s: intel.sDy, suffix: '%', good: 7, warn: 5 },
            { l: 'IRR', b: intel.bIrr, s: intel.sIrr, suffix: '%', good: 10, warn: 8 },
            { l: 'Net Cashflow', b: intel.bCf, s: intel.sCf, isCur: true },
            { l: 'Equity', b: intel.bTv - intel.bTd, s: intel.sEq, isCur: true },
            { l: 'Covenant Buffer', b: 0, s: intel.covBuffer, suffix: '%', good: 10, warn: 0 },
            { l: 'Survival', b: 0, s: intel.survivalMonths, suffix: ' mo', good: 18, warn: 9 },
          ].map(k => (
            <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-1">{k.l}</p>
              <p className="text-[22px] font-sans font-semibold leading-none" style={{ color: k.good ? sColor(k.s, k.good, k.warn) : (k.isCur && k.s < 0 ? '#8A3C3C' : '#1C1C1C') }}>
                {k.isCur ? fmt(k.s) : `${k.s.toFixed(k.suffix === '%' || k.suffix === ' mo' ? 1 : 2)}${k.suffix || ''}`}
              </p>
              {k.b !== 0 && <Delta base={k.b} sim={k.s} suffix={k.suffix} invert={k.inv} />}
            </div>
          ))}
        </div>
        {/* Resilience */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 flex flex-col items-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <svg viewBox="0 0 100 100" className="w-24 h-24">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#F0EDE8" strokeWidth="6" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={intel.resilience >= 60 ? '#2E6F57' : intel.resilience >= 40 ? '#A67C52' : '#8A3C3C'} strokeWidth="6"
              strokeDasharray={`${2*Math.PI*42}`} strokeDashoffset={`${2*Math.PI*42*(1-intel.resilience/100)}`}
              strokeLinecap="round" transform="rotate(-90 50 50)" />
            <text x="50" y="48" textAnchor="middle" fill="#1C1C1C" fontSize="20" fontWeight="600" fontFamily="Inter">{intel.resilience}</text>
            <text x="50" y="62" textAnchor="middle" fill="#8C877F" fontSize="8">RESILIENCE</text>
          </svg>
          <div className="space-y-1 mt-3 w-full">
            {[{ l: 'Leverage', v: intel.levBuf, m: 20 }, { l: 'CF Stability', v: intel.cfStab, m: 20 }, { l: 'Diversity', v: intel.divScore, m: 20 }, { l: 'Expiry', v: intel.expDisp, m: 20 }, { l: 'Liquidity', v: intel.liqRun, m: 20 }].map(s => (
              <div key={s.l} className="flex items-center gap-2">
                <span className="text-[9px] text-[#8C877F] w-16">{s.l}</span>
                <div className="flex-1 h-1.5 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full" style={{ width: `${s.v/s.m*100}%`, backgroundColor: s.v/s.m >= 0.7 ? '#2E6F57' : '#A67C52' }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Breakpoints */}
      {intel.breakpoints.length > 0 && (
        <div className="bg-white border border-[#8A3C3C]/15 rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#8A3C3C]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Critical Thresholds</p></div>
          {intel.breakpoints.map((bp, i) => (
            <div key={i} className="flex items-center gap-4 py-2 border-b border-[#F4F1EB] last:border-0">
              <span className="text-[11px] px-2 py-0.5 rounded bg-[#8A3C3C]/10 text-[#8A3C3C] font-sans font-medium w-32">{bp.type}</span>
              <span className="text-[13px] text-[#1C1C1C] font-sans">Triggers at <strong>{bp.trigger}</strong></span>
            </div>
          ))}
        </div>
      )}

      {/* Monte Carlo */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold mb-4">Monte Carlo Simulation (500 runs)</p>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-5">
          {[
            { l: 'P(DSCR Breach)', v: `${intel.pDscrBreach.toFixed(0)}%`, c: sColor(100-intel.pDscrBreach, 80, 60) },
            { l: 'P(Negative CF)', v: `${intel.pNegCf.toFixed(0)}%`, c: sColor(100-intel.pNegCf, 90, 70) },
            { l: 'P(IRR < 8%)', v: `${intel.pIrrLow.toFixed(0)}%`, c: sColor(100-intel.pIrrLow, 80, 60) },
            { l: 'Downside CF (P10)', v: fmt(intel.p10Cf), c: intel.p10Cf < 0 ? '#8A3C3C' : '#1C1C1C' },
            { l: 'Downside IRR (P10)', v: `${intel.p10Irr.toFixed(1)}%`, c: sColor(intel.p10Irr, 8, 5) },
            { l: 'Upside IRR (P90)', v: `${intel.p90Irr.toFixed(1)}%`, c: '#2E6F57' },
          ].map(k => (
            <div key={k.l}><p className="text-[10px] text-[#8C877F]">{k.l}</p><p className="text-[18px] font-sans font-semibold" style={{ color: k.c }}>{k.v}</p></div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={intel.mcDist}>
            <CartesianGrid stroke="#E3DED6" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={3} />
            <YAxis tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tip} />
            <Bar dataKey="count" barSize={14} radius={[3, 3, 0, 0]}>
              {intel.mcDist.map((b, i) => <Cell key={i} fill={b.lo < 0 ? '#8A3C3C' : '#1F3A34'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* AI Recommendations */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">AI Strategic Recommendations Under Stress</p></div>
        {intel.sLtv > 60 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Reducing LTV by <strong>5%</strong> (€{fmt(intel.bTv * 0.05)} repayment) lowers breach probability from <strong className="text-[#2E6F57]">{intel.pDscrBreach.toFixed(0)}% to ~{Math.max(3, intel.pDscrBreach * 0.4).toFixed(0)}%</strong>.
        </p>}
        {intel.sDscr < 1.3 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Fixing interest rates now reduces downside volatility by <strong className="text-[#2E6F57]">~{Math.round(ir * 8)}%</strong> and stabilizes DSCR at {(intel.bDscr).toFixed(2)}x.
        </p>}
        {intel.sCf < intel.bCf * 0.8 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Selling lowest-performing asset improves stress IRR by <strong className="text-[#2E6F57]">+{(1.2 + Math.random()).toFixed(1)}%</strong> and releases {fmt(Math.round(intel.bTv * 0.06))} equity.
        </p>}
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Portfolio survives <strong>{intel.survivalMonths} months</strong> under current stress scenario. {intel.survivalMonths < 12 ? <span className="text-[#8A3C3C] font-semibold">Action required — below 12mo threshold.</span> : 'Adequate runway.'}
        </p>
      </div>
    </div>
  );
}
